import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
} from 'react';

import { Chip, Stack } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridRowParams,
  GridColumnVisibilityModel,
  GridSortModel,
  GridDensity,
  GridColumnResizeParams,
  useGridApiRef,
  GridColDef,
  GridApiPro,
  MuiEvent,
} from '@mui/x-data-grid-pro';
import scssVariables from '@styles/variables.module.scss';
import { useTranslation } from 'react-i18next';

import styles from './BaseDataTable.module.scss';
import { NoRowsState } from '@/components/ui/NoRowsState';
import type { ColumnWidths } from '@/hooks/grid';
import { useLocaleStore } from '@/stores/locale';

export type BaseDataTableProps<TRow extends object> = {
  items: TRow[];
  isLastPage: boolean;
  showMore: () => void | Promise<unknown>;
  getRowId: (row: TRow) => number;
  onRowClick?: (
    params: GridRowParams<TRow>,
    event: MuiEvent<MouseEvent<HTMLElement>>,
  ) => void;
  columns: GridColDef[];
  totalResults: number;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (model: GridColumnVisibilityModel) => void;
  columnWidths?: ColumnWidths;
  onColumnWidthChange?: (params: GridColumnResizeParams) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (model: GridSortModel) => void;
  density?: GridDensity;
  // Custom column order handler that receives apiRef instead of params
  onColumnOrderChange?: (apiRef: { current: GridApiPro | null }) => void;

  /**
   * Optional callback for arrow key navigation between rows.
   * Called when user presses Arrow Up/Down with a row focused.
   * Receives the target row to navigate to.
   *
   * @example
   * <BaseDataTable
   *   onRowNavigate={(row) => handleViewDetails(String(row.id))}
   * />
   */
  onRowNavigate?: (row: TRow) => void;

  /**
   * Optional callback for Ctrl+Enter toggle behavior on a row.
   * Called when user presses Ctrl+Enter (or Cmd+Enter on Mac).
   * Receives the current row.
   *
   * When Shift is also held (Ctrl+Shift+Enter), shouldFocus will be true,
   * indicating the consumer should move focus to the opened panel.
   *
   * @example
   * <BaseDataTable
   *   onRowToggle={(row, options) => {
   *     togglePanel(String(row.id));
   *     if (options?.shouldFocus) {
   *       focusPanel();
   *     }
   *   }}
   * />
   */
  onRowToggle?: (row: TRow, options?: { shouldFocus: boolean }) => void;

  /**
   * Optional selected row ID for persistent highlighting.
   * When provided, the row with this ID will be highlighted even when focus moves away.
   *
   * @example
   * <BaseDataTable
   *   selectedRowId="123"
   * />
   */
  selectedRowId?: string | number | null;
} & Omit<Partial<DataGridProProps<TRow>>, 'onColumnOrderChange'>; // Exclude MUI's onColumnOrderChange to use our custom signature

export const BaseDataTable = <TRow extends object>({
  items,
  isLastPage,
  showMore,
  getRowId,
  onRowClick,
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnWidths: _columnWidths,
  onColumnWidthChange,
  onColumnOrderChange,
  sortModel,
  onSortModelChange,
  density = 'compact',
  onRowNavigate,
  onRowToggle,
  selectedRowId,
  totalResults,
  ...restProps
}: BaseDataTableProps<TRow>) => {
  const { lang: currentLang } = useLocaleStore();

  const apiRef = useGridApiRef();
  const { t } = useTranslation('ui');
  const loadingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTableFocused, setIsTableFocused] = useState(() =>
    Boolean(
      containerRef.current?.contains(document.activeElement) || selectedRowId,
    ),
  );

  /**
   * Bridge MUI's onColumnOrderChange to parent handler that needs apiRef
   *
   * MUI's onColumnOrderChange receives GridColumnOrderChangeParams, but MUI's
   * official recipe recommends ignoring these params and instead using
   * gridColumnFieldsSelector(apiRef) to get the complete column order.
   *
   * @see https://mui.com/x/react-data-grid/column-recipes/#persisting-column-width-and-order
   *
   * This wrapper captures the table's apiRef and passes it to the parent's
   * handler, maintaining proper separation of concerns:
   * - BaseDataTable owns the DataGrid and its apiRef
   * - Parent manages column order state and persistence
   */
  const handleColumnOrderChangeInternal = useCallback(() => {
    if (onColumnOrderChange) {
      onColumnOrderChange(apiRef);
    }
  }, [onColumnOrderChange, apiRef]);

  /**
   * Handles keyboard navigation in the table.
   * Composes consumer's onCellKeyDown with built-in navigation.
   */
  const handleCellKeyDown = useCallback<
    NonNullable<DataGridProProps<TRow>['onCellKeyDown']>
  >(
    (params, event, details) => {
      // Call consumer's handler first
      if (restProps.onCellKeyDown) {
        restProps.onCellKeyDown(params, event, details);
      }

      // If consumer prevented default, skip built-in navigation
      if (event.defaultMuiPrevented) {
        return;
      }

      // Built-in Ctrl+Enter and Ctrl+Shift+Enter toggle
      if (
        (event.ctrlKey || event.metaKey) &&
        event.key === 'Enter' &&
        onRowToggle
      ) {
        // Skip if focus is on a link - preserve native "open in new tab" behavior
        const target = event.target as HTMLElement;
        if (target.tagName === 'A') {
          return; // Let the browser handle Ctrl+Enter on links
        }

        event.preventDefault();
        event.defaultMuiPrevented = true;
        const shouldFocus = event.shiftKey;
        onRowToggle(params.row, { shouldFocus });
        return;
      }

      // Built-in arrow key navigation
      if (
        (event.key === 'ArrowUp' || event.key === 'ArrowDown') &&
        onRowNavigate
      ) {
        if (!apiRef.current) return;

        const visibleRows = apiRef.current.getAllRowIds();
        const currentIndex = visibleRows.indexOf(params.id);

        if (currentIndex === -1) return;

        const isArrowDown = event.key === 'ArrowDown';
        const targetIndex = isArrowDown ? currentIndex + 1 : currentIndex - 1;

        // Check if target row exists
        if (targetIndex >= 0 && targetIndex < visibleRows.length) {
          event.preventDefault();
          event.defaultMuiPrevented = false; // Allow MUI to move focus

          const targetRowId = visibleRows[targetIndex];
          const targetRow = apiRef.current.getRow(targetRowId);

          if (targetRow) {
            onRowNavigate(targetRow);
          }
        }
      }
    },
    [apiRef, onRowNavigate, onRowToggle, restProps.onCellKeyDown],
  );

  // Scroll to selected row when selectedRowId changes or items load.
  // If the row isn't loaded yet, keep fetching pages until it appears or isLastPage.
  useEffect(() => {
    if (selectedRowId == null || !apiRef.current?.getAllRowIds) return;

    const allRowIds = apiRef.current.getAllRowIds();
    const rowIndex = allRowIds.findIndex(
      (id) => String(id) === String(selectedRowId),
    );

    if (rowIndex !== -1) {
      // Defer until after the grid has finished painting the updated rows
      requestAnimationFrame(() => {
        apiRef.current?.scrollToIndexes({ rowIndex });
      });
    } else if (!isLastPage && !loadingRef.current) {
      loadingRef.current = true;
      Promise.resolve(showMore()).finally(() => {
        loadingRef.current = false;
      });
    }
  }, [selectedRowId, items, isLastPage, showMore]);

  // Reset loading flag when new items arrive
  useEffect(() => {
    loadingRef.current = false;
  }, [items.length]);

  // Handle infinite scroll by listening to scroll events
  useEffect(() => {
    const handleScroll = (event: Event) => {
      if (isLastPage || loadingRef.current) return;

      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;
      const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

      // Load more when within 300px of bottom
      if (distanceToBottom < 300) {
        loadingRef.current = true;
        Promise.resolve(showMore()).finally(() => {
          loadingRef.current = false;
        });
      }
    };

    const gridElement = apiRef.current?.rootElementRef?.current;
    const virtualScroller = gridElement?.querySelector(
      '.MuiDataGrid-virtualScroller',
    );

    if (virtualScroller) {
      virtualScroller.addEventListener('scroll', handleScroll);
      return () => virtualScroller.removeEventListener('scroll', handleScroll);
    }
  }, [apiRef, isLastPage, showMore]);

  // Sync initial focus state on mount
  useEffect(() => {
    setIsTableFocused(
      Boolean(containerRef.current?.contains(document.activeElement)),
    );
  }, []);

  // Track focus state of the table - only show persistent highlight when unfocused
  // Only track focus if hasRowNavigation is enabled
  useEffect(() => {
    if (!restProps.hasRowNavigation) return;

    const container = containerRef.current;
    if (!container) return;

    const handleFocusIn = () => setIsTableFocused(true);
    const handleFocusOut = (event: FocusEvent) => {
      // Check if focus moved outside the container
      if (!container.contains(event.relatedTarget as Node)) {
        setIsTableFocused(false);
      }
    };

    container.addEventListener('focusin', handleFocusIn);
    container.addEventListener('focusout', handleFocusOut);

    return () => {
      container.removeEventListener('focusin', handleFocusIn);
      container.removeEventListener('focusout', handleFocusOut);
    };
  }, [restProps.hasRowNavigation]);

  return (
    <Stack
      ref={containerRef}
      className={styles.Container}
      sx={
        selectedRowId != null && !isTableFocused
          ? (theme) => ({
              // Use sx prop instead of SCSS to dynamically target the selected row by data-id
              // SCSS can't handle dynamic attribute selectors, and this avoids getRowClassName
              // which would cause React re-renders for all rows on every selection change
              [`& .MuiDataGrid-row[data-id="${selectedRowId}"]`]: {
                backgroundColor: scssVariables.ColorFog20,
                ...theme.applyStyles('dark', {
                  backgroundColor: scssVariables.ColorFog100,
                }),
              },
            })
          : undefined
      }
    >
      {/* /* Result count */}
      <Stack direction="row" justifyContent="flex-end" aria-live="polite">
        <Chip
          label={`${totalResults.toLocaleString(currentLang)} ${t(
            'filter.Result',
            {
              count: totalResults,
            },
          )}`}
          size="small"
          color="fog"
        />
      </Stack>

      <DataGridPro
        apiRef={apiRef}
        rows={items}
        columns={columns}
        hideFooter
        getRowId={getRowId}
        onRowClick={onRowClick}
        pagination={false}
        disableColumnFilter
        disableColumnMenu
        disableRowSelectionOnClick
        disableMultipleColumnsSorting
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={onColumnVisibilityModelChange}
        onColumnWidthChange={onColumnWidthChange}
        onColumnOrderChange={handleColumnOrderChangeInternal}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        sortingMode="server"
        density={density}
        pinnedColumnsSectionSeparator="shadow"
        slots={{
          noRowsOverlay: () => <NoRowsState />,
        }}
        {...restProps}
        onCellKeyDown={
          onRowNavigate || onRowToggle
            ? handleCellKeyDown
            : restProps.onCellKeyDown
        }
      />
    </Stack>
  );
};
